import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db } from '../../firebase';
import { doc, setDoc, collection, deleteDoc, onSnapshot } from 'firebase/firestore';
import './AddCookForm.css';
import { 
  FaUserPlus, FaUtensils, FaUser, FaEnvelope, FaLock, FaEdit, FaTrash, 
  FaCheck, FaTimes, FaExclamationTriangle, FaCheckCircle, FaDownload,
  FaSpinner, FaIdCard, FaUserTag
} from 'react-icons/fa';

const AddCookForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cooks, setCooks] = useState([]);
  const [selectedCook, setSelectedCook] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingCook, setEditingCook] = useState(null);

  useEffect(() => {
    const cooksCollection = collection(db, 'identifications');
    
    const unsubscribe = onSnapshot(cooksCollection, (snapshot) => {
      const cookList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCooks(cookList);
    }, (error) => {
      console.error("Erreur lors de la récupération des cuisiniers : ", error);
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (selectedCook) {
        // Mise à jour uniquement du nom et du prénom
        const cookDoc = doc(db, 'identifications', selectedCook.id);
        await setDoc(cookDoc, {
          ...selectedCook, // Conserver les données existantes
          firstName,
          lastName,
        });

        setSuccess('Nom et prénom mis à jour avec succès !');
      } else {
        // Ajouter un nouveau cuisinier
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, 'identifications', user.uid), {
          email: user.email,
          firstName,
          lastName,
          role: 'cook',
          createdAt: new Date(),
        });
        setSuccess('Cuisinier ajouté avec succès !');
      }

      // Réinitialiser les champs
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setSelectedCook(null);
    } catch (error) {
      setError('Erreur lors de l\'ajout ou de la mise à jour du cuisinier : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cook) => {
    setEditingCook(cook);
    setShowEditPopup(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const cookDoc = doc(db, 'identifications', editingCook.id);
      await setDoc(cookDoc, {
        ...editingCook,
        firstName: editingCook.firstName,
        lastName: editingCook.lastName,
      });

      setSuccess('Cuisinier mis à jour avec succès !');
      setShowEditPopup(false);
      setEditingCook(null);
    } catch (error) {
      setError('Erreur lors de la mise à jour du cuisinier : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serverId) => {
    try {
      // Appeler le backend pour supprimer l'utilisateur dans Firebase Authentication
      const response = await fetch('https://skaneat-app.vercel.app/delete-user', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid: serverId }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la suppression de l’utilisateur.');
      }
  
      // Supprimer l'utilisateur dans Firestore Database
      await deleteDoc(doc(db, 'identifications', serverId));
      await deleteDoc(doc(db, 'horaires', serverId));
  
      setSuccess('Serveur supprimé avec succès !');
    } catch (error) {
      setError('Erreur lors de la suppression du cuisinier : ' + error.message);
    }
  };  

  const exportToCSV = () => {
    const today = new Date().toLocaleDateString('fr-FR');
    const headers = ['Nom', 'Prenom', 'Email', 'Role', 'Date de creation'];
    
    // Formater les données
    const csvData = cooks
      .filter((cook) => cook.role === 'cook')
      .map((cook) => {
        const createdAt = cook.createdAt?.toDate 
          ? cook.createdAt.toDate().toLocaleDateString('fr-FR')
          : 'Non specifie';
        
        return [
          cook.firstName,
          cook.lastName,
          cook.email,
          cook.role,
          createdAt
        ].join(';');
      });
    
    // Assembler le contenu CSV
    const csvContent = [
      headers.join(';'),
      ...csvData
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `cuisiniers_${today.replace(/\//g, '-')}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="cook-management-container">
      {/* Header */}
      <div className="cook-management-header">
        <h1 className="cook-management-title">
          <FaUtensils className="cook-title-icon" />
          Gestion des Cuisiniers
        </h1>
        <p className="cook-management-subtitle">
          Ajoutez et gérez les comptes cuisiniers de votre restaurant
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="cook-message cook-error-message">
          <FaExclamationTriangle className="cook-message-icon" />
          {error}
        </div>
      )}
      {success && (
        <div className="cook-message cook-success-message">
          <FaCheckCircle className="cook-message-icon" />
          {success}
        </div>
      )}

      {/* Form Section */}
      <div className="cook-form-section">
        <h2 className="cook-form-title">
          <FaUserPlus className="cook-form-icon" />
          Ajouter un cuisinier
        </h2>
        
        <form className="cook-form" onSubmit={handleSubmit}>
          <div className="cook-form-row">
            <div className="cook-form-group">
              <label className="cook-form-label">
                <FaUser className="cook-label-icon" />
                Nom
              </label>
              <input
                type="text"
                className="cook-form-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Ex: Martin"
                required
              />
            </div>
            <div className="cook-form-group">
              <label className="cook-form-label">
                <FaIdCard className="cook-label-icon" />
                Prénom
              </label>
              <input
                type="text"
                className="cook-form-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Ex: Pierre"
                required
              />
            </div>
          </div>

          <div className="cook-form-row">
            <div className="cook-form-group">
              <label className="cook-form-label">
                <FaEnvelope className="cook-label-icon" />
                Email du cuisinier
              </label>
              <input
                type="email"
                className="cook-form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Ex: pierre.martin@restaurant.com"
                required
              />
            </div>
            <div className="cook-form-group">
              <label className="cook-form-label">
                <FaLock className="cook-label-icon" />
                Mot de passe du cuisinier
              </label>
              <input
                type="password"
                className="cook-form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe sécurisé"
                required
              />
            </div>
          </div>

          <div className="cook-form-actions">
            <button 
              type="submit" 
              className="cook-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinning" />
                  Ajout en cours...
                </>
              ) : (
                <>
                  <FaCheck />
                  Ajouter un cuisinier
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Cook List Section */}
      <div className="cook-list-section">
        <div className="cook-list-header">
          <h3 className="cook-list-title">
            <FaUserTag className="cook-list-icon" />
            Cuisiniers Enregistrés
          </h3>
          <button onClick={exportToCSV} className="cook-export-btn">
            <FaDownload />
            Exporter en CSV
          </button>
        </div>
        
        {cooks.filter((cook) => cook.role === 'cook').length === 0 ? (
          <div className="cook-empty-state">
            <FaUtensils className="cook-empty-icon" />
            <p className="cook-empty-text">Aucun cuisinier enregistré pour le moment</p>
          </div>
        ) : (
          <ul className="cook-list">
            {cooks
              .filter((cook) => cook.role === 'cook')
              .map((cook) => (
                <li key={cook.id} className="cook-list-item">
                  <div className="cook-info">
                    <div className="cook-name">
                      {cook.firstName} {cook.lastName}
                    </div>
                    <div className="cook-email">
                      {cook.email}
                    </div>
                    <div className="cook-role-badge">
                      Cuisinier
                    </div>
                  </div>
                  <div className="cook-actions">
                    <button 
                      className="cook-action-btn cook-edit-btn"
                      onClick={() => handleEdit(cook)}
                    >
                      <FaEdit />
                      Modifier
                    </button>
                    <button 
                      className="cook-action-btn cook-delete-btn"
                      onClick={() => handleDelete(cook.id)}
                    >
                      <FaTrash />
                      Supprimer
                    </button>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>

      {/* Edit Cook Popup */}
      {showEditPopup && editingCook && (
        <div className="cook-popup-overlay" onClick={() => setShowEditPopup(false)}>
          <div className="cook-popup" onClick={e => e.stopPropagation()}>
            <div className="cook-popup-header">
              <h3 className="cook-popup-title">
                <FaEdit className="cook-popup-icon" />
                Modifier le cuisinier
              </h3>
            </div>
            
            <div className="cook-popup-body">
              <form className="cook-form" onSubmit={handleEditSubmit}>
                <div className="cook-form-row">
                  <div className="cook-form-group">
                    <label className="cook-form-label">
                      <FaUser className="cook-label-icon" />
                      Nom
                    </label>
                    <input
                      type="text"
                      className="cook-form-input"
                      value={editingCook.firstName || ''}
                      onChange={(e) => setEditingCook({
                        ...editingCook,
                        firstName: e.target.value
                      })}
                      placeholder="Ex: Martin"
                      required
                    />
                  </div>
                  <div className="cook-form-group">
                    <label className="cook-form-label">
                      <FaIdCard className="cook-label-icon" />
                      Prénom
                    </label>
                    <input
                      type="text"
                      className="cook-form-input"
                      value={editingCook.lastName || ''}
                      onChange={(e) => setEditingCook({
                        ...editingCook,
                        lastName: e.target.value
                      })}
                      placeholder="Ex: Pierre"
                      required
                    />
                  </div>
                </div>

                <div className="cook-form-group">
                  <label className="cook-form-label">
                    <FaEnvelope className="cook-label-icon" />
                    Email
                  </label>
                  <input
                    type="email"
                    className="cook-form-input"
                    value={editingCook.email || ''}
                    disabled
                    style={{ background: 'var(--cook-bg-color)', color: 'var(--cook-text-secondary)' }}
                  />
                  <small style={{ color: 'var(--cook-text-secondary)', fontSize: '0.75rem' }}>
                    L'email ne peut pas être modifié
                  </small>
                </div>
              </form>
            </div>

            <div className="cook-popup-actions">
              <button 
                className="cook-popup-btn primary" 
                onClick={handleEditSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spinning" />
                    Mise à jour...
                  </>
                ) : (
                  <>
                    <FaCheck />
                    Mettre à jour
                  </>
                )}
              </button>
              <button 
                className="cook-popup-btn secondary" 
                onClick={() => {
                  setShowEditPopup(false);
                  setEditingCook(null);
                }}
              >
                <FaTimes />
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCookForm;
